// src/App.js
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./config/Firebase";

// Components
import Navbar from "./components/Navbar";
import Spinner from "./components/Spinner";
import PWABanner from "./PAWBanner";

// Lazy loaded pages
const Home = lazy(() => import("./pages/Home"));
const AllProducts = lazy(() => import("./pages/AllProducts"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const Trending = lazy(() => import("./pages/Trending"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AdminLogin = lazy(() => import("./auth/AdminLogin"));
const AdminDashboard = lazy(() => import("./auth/AdminDashboard"));
const AddProductPage = lazy(() => import("./auth/AddProduct"));
const UpdateProduct = lazy(() => import("./auth/UpdateProduct"));
const OrdersList = lazy(() => import("./components/OrdersList"));
const BlogList = lazy(() => import("./blog/BlogList"));          // list all blogs
const BlogPost = lazy(() => import("./pages/BlogPost"));       // single blog post
const AddBlogPage = lazy(() => import("./blog/AddBlogPage"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Terms = lazy(() => import("./pages/Terms"));
const CustomerSupport = lazy(() => import("./pages/CustomeSupport"));
const NotFound = lazy(() => import("./pages/NotFound"));

const AppContent = () => {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  return (
    <HelmetProvider>
      <div className="app-wrapper">
        <Navbar />
        <PWABanner /> {/* always visible */}

        <main className="main-content">
          <Suspense
            fallback={
              <div className="page-loader">
                <Spinner size="large" />
                <p>Loading...</p>
              </div>
            }
          >
            <Routes>
              {/* Public Pages */}
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<AllProducts />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/trending" element={<Trending />} />
              <Route path="/about" element={<About />} />

              {/* Blogs */}
              <Route path="/blog" element={<BlogList />} /> 
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/admin/add-blog" element={<AddBlogPage />} />

              {/* Policies */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/terms-and-conditions" element={<Terms />} />
              <Route path="/customer-support" element={<CustomerSupport />} />

              {/* Admin Pages */}
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/admin/add-product" element={<AddProductPage />} />
              <Route path="/admin/edit-product/:id" element={<UpdateProduct />} />
              <Route path="/admin/orders" element={<OrdersList />} />
              <Route path="/admin-login" element={<AdminLogin />} />

              {/* 404 Page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </HelmetProvider>
  );
};

const App = () => {
  // Track auth state (optional: can be used for protected admin routes)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", user?.email);
    });
    return () => unsubscribe();
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
